import React, { useEffect, useState } from "react";

import HttpManager from "../../services/HttpManager";
import { RotativaOptionsDTO } from "../../models/EnrollmentHistoryDTO";
import { horarioBonito } from "../../utils/horarioBonito";
import "./rotativaPicker.css";

export interface RotativaPickerProps {
	campusId: number;
	/** Horario de casa (el que se matricula). */
	scheduleId: number;
	/** null = no es rotativa; [] = rotativa sin horarios elegidos todavía. */
	value: number[] | null;
	onChange: (value: number[] | null) => void;
	disabled?: boolean;
	/** Marcar lo que falta (al intentar avanzar). */
	marcar?: boolean;
}

const http = HttpManager.getInstance();
const corto = (nombre: string) => horarioBonito(nombre, { plural: true, separador: " " });
const horas = (h: number) => (Number.isInteger(h) ? h : h.toFixed(1)) + " h";

/**
 * Rotativa (Esteban, 2026-10-26): la alumna toma clase en varios horarios del MISMO módulo y periodo. Se matricula en su horario
 * de casa y los otros quedan autorizados (salen en su lista de asistencia); las horas por semana no pasan de las de su horario.
 */
const RotativaPicker: React.FC<RotativaPickerProps> = ({ campusId, scheduleId, value, onChange, disabled, marcar }) => {
	const [data, setData] = useState<RotativaOptionsDTO | null>(null);
	const activa = value !== null;

	useEffect(() => {
		if (!activa || data) return;
		let vigente = true;
		http.enrollmentRequestsService.rotativos(campusId, scheduleId)
			.then((r) => vigente && setData(r.data))
			.catch(() => vigente && setData({ home: null, options: [] }));
		return () => { vigente = false; };
	}, [activa, data, campusId, scheduleId]);

	const alternar = () => { if (!disabled) onChange(activa ? null : []); };
	const elegir = (id: number) => {
		if (disabled || value === null) return;
		onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
	};
	const elegidos = (data?.options || []).filter((o) => value?.includes(o.id));

	return (
		<div className="waRotativa">
			<div className="waRotativaFila" onClick={alternar}>
				<div>
					<b>Rotativa</b>
					<span>Toma clase en varios horarios del mismo módulo</span>
				</div>
				<button type="button" role="switch" aria-checked={activa} aria-label="Rotativa" className="waInterruptor" disabled={disabled}
					onClick={(e) => { e.stopPropagation(); alternar(); }} />
			</div>
			{activa ? (
				data === null ? <div className="loader" /> : data.options.length < 1 ? (
					<div className="waRotativaResumen falta">Este módulo no tiene otros horarios en el mismo periodo.</div>
				) : (
					<>
						<div className="waRotativaOpciones">
							{data.home ? (
								<button type="button" className="waPildora casa" aria-pressed="true" disabled>
									{corto(data.home.name)}
									<small>Su horario</small>
								</button>
							) : null}
							{data.options.map((o) => (
								<button key={"rot_" + o.id} type="button" className="waPildora" aria-pressed={!!value?.includes(o.id)} disabled={disabled}
									onClick={() => elegir(o.id)}>
									{corto(o.name)}
									<small>{[o.mentorName, horas(o.weeklyHours) + " por semana"].filter(Boolean).join(" · ")}</small>
								</button>
							))}
						</div>
						<div className={"waRotativaResumen" + (marcar && elegidos.length < 1 ? " falta" : "")}>
							{elegidos.length < 1
								? "Toca los horarios donde también puede tomar clase."
								: `Puede tomar clase en ${elegidos.length + 1} horarios · máximo ${horas(data.home?.weeklyHours || 0)} por semana en total`}
						</div>
					</>
				)
			) : null}
		</div>
	);
};

export default RotativaPicker;
